import { capitalize } from "@mui/material";
import { memo, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { BooleanSwitchValue } from "consts/general";
import useMerchantPaymentMethods from "./useMerchantPaymentMethods";
import { useTypedSelector } from "hooks";
import { selectMerchantMethodPivotActiveDomain } from "features/general/selectors";
import type { Option } from "types";
import BulkEditPaymentMethodLimits from "./BulkEditPaymentMethodLimits";

type Props = {
    selectedIds: Set<string>;
    selectAll: boolean;
    onSelectAll: (checked: boolean) => void;
    onClear: () => void;
    onApplied: (ids: string[], state: { enabled: BooleanSwitchValue; MOR: string }) => void;
};

const BulkMerchantPaymentMethods = ({ selectedIds, selectAll, onSelectAll, onClear, onApplied }: Props) => {
    const { repository, getMerchantPaymentMethods } = useMerchantPaymentMethods();
    const filteredMethods = getMerchantPaymentMethods();
    const domainOptions = useTypedSelector(selectMerchantMethodPivotActiveDomain);
    const [value, setValue] = useState(`${BooleanSwitchValue.Off}`);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const apply = async () => {
        setProcessing(true);
        setError('');
        try {
            const selectedMethods = filteredMethods.filter(method => selectedIds.has(String(method.coreId)));
            const unavailable = selectedMethods.filter(method => !method.paymentMethodIsAssignable);
            if (unavailable.length) {
                throw new Error(`${unavailable.length} selected method(s) cannot be edited because they are not assignable in Payment Methods Setup.`);
            }
            for (const method of selectedMethods) {
                await repository.toggleEnableMerchantsMethodsPivot({
                    coreId: method.coreId,
                    enabled: value === `${BooleanSwitchValue.Off}`
                        ? BooleanSwitchValue.Off
                        : BooleanSwitchValue.On,
                    MOR: value
                });
            }
            onApplied(selectedMethods.map(method => String(method.coreId)), {
                enabled: value === `${BooleanSwitchValue.Off}` ? BooleanSwitchValue.Off : BooleanSwitchValue.On,
                MOR: value
            });
            window.dispatchEvent(new CustomEvent('merchant-payment-methods-bulk-updated', {
                detail: {
                    ids: selectedMethods.map(method => String(method.coreId)),
                    state: {
                        enabled: value === `${BooleanSwitchValue.Off}` ? BooleanSwitchValue.Off : BooleanSwitchValue.On,
                        MOR: value
                    }
                }
            }));
            onClear();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'The bulk update could not be applied.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
            <FormControlLabel
                control={<Checkbox checked={selectAll} onChange={(_, checked) => onSelectAll(checked)} />}
                label={`Select all filtered (${filteredMethods.length})`}
            />
            <TextField
                select
                size='small'
                label='Set status / MOR'
                value={value}
                onChange={event => setValue(event.target.value)}
                sx={{ minWidth: 180 }}
            >
                <MenuItem value={`${BooleanSwitchValue.Off}`}>Disable</MenuItem>
                {domainOptions.map((option: Option) => (
                    <MenuItem key={String(option.id)} value={String(option.id)}>
                        {capitalize(option.name)}
                    </MenuItem>
                ))}
            </TextField>
            <LoadingButton
                variant='outlined'
                loading={processing}
                disabled={!selectedIds.size}
                onClick={apply}
            >
                Apply
            </LoadingButton>
            <BulkEditPaymentMethodLimits selectedIds={selectedIds} onClear={onClear} />
            <Typography variant='caption' color={error ? 'error' : 'text.secondary'}>
                {error || `${selectedIds.size} selected`}
            </Typography>
        </Box>
    );
};

export default memo(BulkMerchantPaymentMethods);
